import { BillingStrategy } from '@ctct/domain-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import { PaymentStrategy } from '@ctct/state-server-layer';

import {
  AmazonPayService,
  PaymentMethodsService,
  InHouseApiPayloadService,
  PaypalService,
  SecurePayService,
  ApplePayService,
  StripeService,
} from './services';
import { PaymentMethodsDataPersistenceService } from './services/PaymentMethodsDataPersistenceService';
import { StripeSavedPaymentMethodService } from './services/StripeSavedPaymentMethodService';
import type { Init, PaymentMethodsUrlAdapter } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type PaymentMethodsData = MergedDataReturnType<
  [AmazonPayService, PaymentMethodsService, PaypalService, SecurePayService, ApplePayService]
>;

export type StripePaymentMethodsData = MergedDataReturnType<[StripeService, StripeSavedPaymentMethodService]>;

export type PaymentMethodsHandlers = MergedHandlersReturnType<
  [AmazonPayService, PaymentMethodsService, PaypalService, SecurePayService, ApplePayService]
>;

export type StripePaymentMethodsHandlers = MergedHandlersReturnType<[StripeService, StripeSavedPaymentMethodService]>;

type TInHouseApi = ReturnType<PaymentMethodsService['api']> & { billingStrategy: BillingStrategy } & ReturnType<
    InHouseApiPayloadService['api']
  > &
  ReturnType<ApplePayService['api']>;

type TStripeApi = ReturnType<PaymentMethodsService['api']> & { billingStrategy: BillingStrategy } & ReturnType<
    StripeService['api']
  > &
  ReturnType<PaypalService['api']>;

type TApi =
  | ({ paymentStrategy: PaymentStrategy.Stripe } & TStripeApi)
  | ({ paymentStrategy: PaymentStrategy.Paas } & TInHouseApi);

export type PaymentMethodsStripeData = MergedDataReturnType<[StripeService]>;

export type PaymentMethodsStripeHandlers = MergedHandlersReturnType<[StripeService]>;

const performanceTracker = observabilityAppService.createPerformanceTracker('paymentMethodsInit', {
  tags: ['application-layer', 'PaymentMethodsManager'],
});

export class PaymentMethodsManager extends WithApi<TApi> {
  constructor(private urlAdapter: PaymentMethodsUrlAdapter) {
    super();
  }

  public paymentMethodsInit = ({
    pageStore,
    queries,
    mutations,
    isApplePayEnabled,
    isImpersonated = false,
    isNewPayflow = false,
    isNewCustomerPayflow = false,
    t,
    billingStrategy,
    paymentStrategy,
    stripeLocaleCountryCode,
    flags,
  }: Init) => {
    const [
      billingInfoQuery,
      billingAccountQuery,
      partnerNameQuery,
      isAmexIrAffiliateQuery,
      elementsCredentialsQuery,
      ratingQuery,
      stripeCustomerQuery,
    ] = queries;
    const error =
      billingInfoQuery.error ||
      billingAccountQuery.error ||
      partnerNameQuery.error ||
      isAmexIrAffiliateQuery.error ||
      elementsCredentialsQuery.error ||
      (!flags?.shouldIgnoreRatingCall ? ratingQuery.error : undefined);

    if (error) {
      performanceTracker.reportError({
        error,
        meta: {
          'billingInfoQuery.error': !!billingInfoQuery.error,
          'billingAccountQuery.error': !!billingAccountQuery.error,
          'partnerNameQuery.error': !!partnerNameQuery.error,
          'isAmexIrAffiliateQuery.error': !!isAmexIrAffiliateQuery.error,
          'elementsCredentialsQuery.error': !!elementsCredentialsQuery.error,
          'ratingQuery.error': !!ratingQuery.error,
          'stripeCustomerQuery.error': !!stripeCustomerQuery.error,
        },
      });

      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
        lifecycle: null,
      } as const;
    }

    const pending =
      !paymentStrategy.data ||
      paymentStrategy.isPending ||
      billingInfoQuery.isPending ||
      billingAccountQuery.isPending ||
      partnerNameQuery.isPending ||
      isAmexIrAffiliateQuery.isPending;

    if (pending) {
      performanceTracker.reportLoading();

      return {
        status: 'pending',
        data: null,
        handlers: null,
        lifecycle: null,
      } as const;
    }

    const isStripePaymentStrategy = paymentStrategy.data === PaymentStrategy.Stripe;

    const [
      createPaypalTokenMutation,
      createPaypalAgreementMutation,
      createAmazonPaySignatureMutation,
      createAmazonPaySessionMutation,
      updateSetupIntentParamsMutation,
    ] = mutations;

    const paymentMethodsService = new PaymentMethodsService(
      pageStore,
      billingInfoQuery.data,
      billingAccountQuery.data,
      t,
      isApplePayEnabled
    );

    const securePayService = new SecurePayService(
      pageStore,
      billingInfoQuery.data,
      partnerNameQuery.data,
      isAmexIrAffiliateQuery.data
    );

    const applePayService = new ApplePayService(pageStore, billingInfoQuery.data, billingAccountQuery.data, t);

    const paypalService = new PaypalService(
      pageStore,
      billingInfoQuery.data,
      createPaypalTokenMutation,
      createPaypalAgreementMutation,
      this.urlAdapter,
      isNewPayflow,
      isNewCustomerPayflow,
      isStripePaymentStrategy,
      billingStrategy === BillingStrategy.Stripe
    );

    const amazonPayService = new AmazonPayService(
      pageStore,
      billingInfoQuery.data,
      createAmazonPaySignatureMutation,
      createAmazonPaySessionMutation,
      this.urlAdapter,
      isNewPayflow,
      isNewCustomerPayflow
    );

    const inHouseApiPayloadService = new InHouseApiPayloadService(
      pageStore,
      billingInfoQuery.data,
      paymentMethodsService,
      securePayService,
      amazonPayService,
      paypalService,
      applePayService
    );

    const persistence = new PaymentMethodsDataPersistenceService(pageStore, billingAccountQuery.data);

    if (isStripePaymentStrategy) {
      performanceTracker.addTag('stripe');

      // for rating query, we need to use `!isFetched` so we don't force the UI back into a loading state
      // when the rating query changes.
      if ((!flags?.shouldIgnoreRatingCall && !ratingQuery.data) || !elementsCredentialsQuery.data) {
        performanceTracker.reportLoading();

        return {
          status: 'pending',
          data: null,
          handlers: null,
          lifecycle: null,
        } as const;
      }

      // Wait for stripeCustomerQuery to finish loading otherwise we might not show the saved payment method for customer
      // we use isLoading instead of isPending to avoid blocking the UI if the query is skipped (i.e. for trailers)

      if (stripeCustomerQuery.isLoading) {
        performanceTracker.reportLoading();

        return {
          status: 'pending',
          data: null,
          handlers: null,
          lifecycle: null,
        } as const;
      }

      performanceTracker.reportLoaded();

      const stripeService = new StripeService(
        pageStore,
        elementsCredentialsQuery.data,
        paypalService,
        stripeCustomerQuery.data ?? null,
        billingAccountQuery.data,
        billingStrategy,
        updateSetupIntentParamsMutation,
        stripeLocaleCountryCode,
        isImpersonated,
        ratingQuery.refetch
      );
      const stripeSavedPaymentMethodService = new StripeSavedPaymentMethodService(
        stripeCustomerQuery.data ?? null,
        pageStore
      );

      this.initApi({
        billingStrategy,
        paymentStrategy: PaymentStrategy.Stripe,
        ...paymentMethodsService.api(),
        ...stripeService.api(),
        ...paypalService.api(),
      });

      return {
        status: 'loaded',
        billingStrategy,
        paymentStrategy: PaymentStrategy.Stripe,
        data: {
          ...stripeService.data(),
          ...stripeSavedPaymentMethodService.data(),
        },
        handlers: {
          ...stripeService.handlers(),
          ...stripeSavedPaymentMethodService.handlers(),
        },
        lifecycle: {
          onMount() {
            stripeService.onMount();
          },
        } satisfies WidgetLifecycle,
      } as const;
    }

    performanceTracker.removeTag('stripe');

    performanceTracker.reportLoaded();

    this.initApi({
      billingStrategy,
      paymentStrategy: PaymentStrategy.Paas,
      ...paymentMethodsService.api(),
      ...paypalService.api(),
      ...inHouseApiPayloadService.api(),
      ...applePayService.api(),
    });

    return {
      status: 'loaded',
      billingStrategy,
      paymentStrategy: PaymentStrategy.Paas,
      data: {
        ...paymentMethodsService.data(),
        ...securePayService.data(),
        ...applePayService.data(),
        ...paypalService.data(),
        ...amazonPayService.data(),
        ...persistence.data(),
      },
      handlers: {
        ...paymentMethodsService.handlers(),
        ...securePayService.handlers(),
        ...applePayService.handlers(),
        ...paypalService.handlers(),
        ...amazonPayService.handlers(),
        ...persistence.handlers(),
      },
      lifecycle: {
        onMount() {
          paymentMethodsService.init();
        },
        onUnmountOrUnload() {
          persistence.persistPaymentMethodsData();
          performanceTracker.dispose();
        },
      } satisfies WidgetLifecycle,
    } as const;
  };
}
